/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	Badge,
	Button,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	SimpleGrid,
	Text,
	Tooltip,
	useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CompanyName from '../../../components/CompanyName';
import DownloadAndViewButton from '../../../components/Files/DownloadAndViewButton';
import LabelDetail from '../../../components/LabelDetail';
import c from '../../../constant';
import useMoment from '../../../hooks/useMoment';
import useStore from '../../../store';
import { getReportName, getTypeName, getTypeValue } from '../Common/Utils';
import {
	mdMonthlyClaim,
	mdMonthlyDistribution,
	mdMonthlySale,
	mdQuarterlyClaim,
	mdQuarterlySale,
	sdQuarterlyFinance,
	sdQuarterlyInvestment,
} from '../Common/Constants';

const Q_GET = gql`
	query GetReportDueById($id: String!) {
		getReportDueById(id: $id) {
			id
			createdAt
			creator {
				id
				username
				fullName
				fullNameKhmer
				avatar
			}
			updatedAt
			updater {
				id
				username
				fullName
				fullNameKhmer
				avatar
			}
			type
			year
			typeValue
			dueDate
			company {
				id
				name
				nameKhmer
				type
				logo
			}
			attachment
			active
			fileReport {
				id
				title
				titleKh
				departments
				period
			}
		}
	}
`;

const M_CLOSE = gql`
	mutation CloseReportDue($id: String!) {
		closeReportDue(id: $id) {
			id
			createdAt
			creator {
				id
				username
				fullName
				fullNameKhmer
				avatar
			}
			updatedAt
			updater {
				id
				username
				fullName
				fullNameKhmer
				avatar
			}
			type
			year
			typeValue
			dueDate
			company {
				id
				name
				nameKhmer
				type
				logo
			}
			attachment
			active
		}
	}
`;

function Detail({ id = null, onClose = () => null, onComplete = () => null }) {
	const toast = useToast();
	const { t } = useTranslation();
	const { toDateString } = useMoment();
	const { currentLang: lang } = useStore((state) => state?.currentLang);
	const [getReportDueById, { loading: loadingGet, data: dataGet }] = useLazyQuery(Q_GET);
	const [closeReportDue, { loading: loadingClose }] = useMutation(M_CLOSE);
	const isMonthly = useMemo(
		() =>
			dataGet?.getReportDueById?.type === mdMonthlySale?.value ||
			dataGet?.getReportDueById?.type === mdMonthlyClaim?.value ||
			dataGet?.getReportDueById?.type === mdMonthlyDistribution?.value,

		[dataGet],
	);
	const isQuarterly = useMemo(
		() =>
			dataGet?.getReportDueById?.type === mdQuarterlySale?.value ||
			dataGet?.getReportDueById?.type === mdQuarterlyClaim?.value ||
			dataGet?.getReportDueById?.type === sdQuarterlyFinance?.value ||
			dataGet?.getReportDueById?.type === sdQuarterlyInvestment?.value,

		[dataGet],
	);

	const isFileReport = useMemo(() => dataGet?.getReportDueById?.fileReport !== null && dataGet?.getReportDueById?.fileReport !== undefined, [dataGet]);

	useEffect(load, [id]);

	function load() {
		if (id) {
			const variables = { id };
			getReportDueById({ variables });
		}
	}

	return (
		<Modal
			closeOnEsc={loadingGet || loadingClose ? false : true}
			closeOnOverlayClick={loadingGet || loadingClose ? false : true}
			isOpen={true}
			onClose={onClose}
			size="3xl"
		>
			<ModalOverlay />
			<ModalContent borderRadius={c.borderRadius}>
				<ModalHeader pb={0} mb={0}>
					{t('Detail')}
				</ModalHeader>
				<ModalCloseButton isDisabled={loadingGet || loadingClose} />
				<ModalBody>
					{dataGet?.getReportDueById && (
						<SimpleGrid columns={[1, null, 2]} spacing={2}>
							<LabelDetail
								label="Creator"
								titleDetail={dataGet?.getReportDueById?.creator?.fullName || dataGet?.getReportDueById?.creator?.username}
							/>
							<LabelDetail label="Create At" titleDetail={toDateString(dataGet?.getReportDueById?.createdAt, c.dateTimeFormat)} />
							{dataGet?.getReportDueById?.updater && (
								<LabelDetail
									label="Updated By"
									titleDetail={dataGet?.getReportDueById?.updater?.fullName || dataGet?.getReportDueById?.updater?.username}
								/>
							)}
							{dataGet?.getReportDueById?.updatedAt && (
								<LabelDetail label="Updated At" titleDetail={toDateString(dataGet?.getReportDueById?.updatedAt, c.dateTimeFormat)} />
							)}
							<LabelDetail label="Company" childDetail={<CompanyName company={dataGet?.getReportDueById?.company} />} />
							<LabelDetail label="Type" childDetail={getReportName(dataGet?.getReportDueById?.type, lang)} />
							{dataGet?.getReportDueById?.year && <LabelDetail label="Year" titleDetail={dataGet?.getReportDueById?.year} />}
							{isMonthly && (
								<LabelDetail
									label="Month"
									titleDetail={getTypeValue(
										dataGet?.getReportDueById?.type,
										dataGet?.getReportDueById?.typeValue,
										dataGet?.getReportDueById?.fileReport,
										lang,
										t,
									)}
								/>
							)}
							{isQuarterly && (
								<LabelDetail
									label="Quarter"
									titleDetail={getTypeValue(
										dataGet?.getReportDueById?.type,
										dataGet?.getReportDueById?.typeValue,
										dataGet?.getReportDueById?.fileReport,
										lang,
										t,
									)}
								/>
							)}
							{isFileReport && (
								<LabelDetail
									label="Report"
									titleDetail={getTypeValue(
										dataGet?.getReportDueById?.type,
										dataGet?.getReportDueById?.typeValue,
										dataGet?.getReportDueById?.fileReport,
										lang,
										t,
									)}
								/>
							)}
							<LabelDetail label="Due Date" titleDetail={toDateString(dataGet?.getReportDueById?.dueDate, c.dateTimeFormat)} />
							<LabelDetail
								label="Attachment"
								childDetail={
									<DownloadAndViewButton
										marginLeft={0}
										url={dataGet?.getReportDueById?.attachment?.file}
										size={dataGet?.getReportDueById?.attachment?.fileSize}
									/>
								}
							/>
							<LabelDetail
								label="Status"
								childDetail={
									<Badge colorScheme={dataGet?.getReportDueById?.active === true ? 'green' : 'red'}>
										{dataGet?.getReportDueById?.active === true ? t('Active') : t('Inactive')}
									</Badge>
								}
							/>
						</SimpleGrid>
					)}
				</ModalBody>
				<ModalFooter>
					{dataGet?.getReportDueById?.active === true && (
						<Button
							colorScheme="red"
							onClick={() => {
								const variables = { id };
								closeReportDue({ variables })
									.then(() => {
										onClose();
										onComplete();
									})
									.catch((error) => {
										toast({
											title: t(error?.message ? error?.message : 'something went wrong'),
											status: 'error',
											isClosable: true,
											position: 'bottom-right',
										});
									});
							}}
							isLoading={loadingClose}
						>
							{t('Deactivate')}
						</Button>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}

export default Detail;
